import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/newsBulletin.css";

const NewsBulletin = () => {
  const [bulletin, setBulletin] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    try {
      const storedBulletin =
        JSON.parse(localStorage.getItem("newsBulletin")) || [];

      console.log("📌 Stored Bulletin Data:", storedBulletin);

      // ✅ Group news by date
      const groupedNews = storedBulletin.reduce((acc, item) => {
        const timestamp = new Date(item.timestamp);
        if (isNaN(timestamp)) {
          console.warn("⚠️ Invalid timestamp found, skipping:", item);
          return acc;
        }

        const formattedDate = timestamp.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        if (!acc[formattedDate]) {
          acc[formattedDate] = [];
        }
        acc[formattedDate].push(item);
        return acc;
      }, {});

      console.log("📌 Grouped News by Date:", groupedNews);
      setBulletin(groupedNews);
    } catch (error) {
      console.error("❌ Error fetching news bulletin:", error);
      setBulletin({});
    }
  }, []);

  // **📌 Hide UI elements before taking screenshot**
  const hideElementsBeforeScreenshot = (hide = true) => {
    console.log(
      "🔄 Toggling visibility for UI elements before screenshot:",
      hide
    );
    document
      .querySelectorAll(".context-menu, .add-to-news-button")
      .forEach((btn) => {
        btn.style.display = hide ? "none" : "block";
      });
  };

  // **📌 Extract Post Description & Link**
  const extractPostDetails = (post) => {
    console.log("🔍 Extracting details for post ID:", post.id);

    // ✅ Extract post text from `.card-body`
    let postText = "No text available.";
    if (post.text) {
      postText =
        post.text.length > 100
          ? `${post.text.substring(0, 100)}...`
          : post.text;
    }
    console.log(`📝 Post Text (ID: ${post.id}):`, postText);

    // ✅ Extract post link from "copy-post-link-button"
    const postLink = post.postURL || "N/A";
    console.log(`🔗 Post Link (ID: ${post.id}):`, postLink);

    return { postText, postLink };
  };

  // **📌 Download Full Page as PDF**
  const downloadPageAsPDF = async () => {
    console.log("📥 Starting PDF generation...");
    const bulletinElement = document.getElementById("bulletin-content");

    if (!bulletinElement) {
      console.error("❌ Cannot find news bulletin content!");
      toast.error("⚠️ Cannot find news bulletin content!");
      return;
    }

    try {
      hideElementsBeforeScreenshot(true); // Hide buttons before screenshot

      const canvas = await html2canvas(bulletinElement, { scale: 2 });
      console.log("📸 Screenshot captured successfully!");

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 190; // Adjust for A4 width
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save(`News_Bulletin_${selectedDate}.pdf`);

      console.log("✅ PDF downloaded successfully!");
      hideElementsBeforeScreenshot(false); // Show buttons after screenshot
    } catch (error) {
      console.error("❌ Error capturing PDF:", error);
      toast.error("❌ Failed to generate PDF.");
    }
  };

  return (
    <div className="news-bulletin-container">
      <div className="news-bulletin">
        <h2>📰 News Bulletin</h2>

        {/* **📅 Date List (Clickable) ** */}
        {!selectedDate && (
          <table className="date-table">
            <thead>
              <tr>
                <th>📅 Date</th>
                <th>📥 View Bulletin</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(bulletin).length > 0 ? (
                Object.keys(bulletin).map((date) => (
                  <tr key={date}>
                    <td className="clickable-date">{date}</td>
                    <td>
                      <button
                        className="view-btn"
                        onClick={() => setSelectedDate(date)}
                      >
                        View News Bulletin
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2" className="no-data">
                    No news available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* **📜 Display News Bulletin for Selected Date ** */}
        {selectedDate && bulletin[selectedDate] && (
          <div id="bulletin-content" className="bulletin-content">
            <h3>📰 News Bulletin for {selectedDate}</h3>
            <table className="bulletin-table">
              <thead>
                <tr>
                  <th>SR #</th>
                  <th>Post Description & Link</th>
                  <th>Post Image</th>
                </tr>
              </thead>
              <tbody>
                {bulletin[selectedDate].map((post, index) => {
                  const { postText, postLink } = extractPostDetails(post);
                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        <p>{postText}</p>
                        <a
                          href={postLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {postLink}
                        </a>
                      </td>
                      <td>
                        {post.imageURL ? (
                          <img
                            src={post.imageURL}
                            alt={`Post ${index + 1}`}
                            className="news-image"
                          />
                        ) : (
                          "No Image Available"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* **Download Full Page as PDF** */}
            <button className="download-btn" onClick={downloadPageAsPDF}>
              📥 Download News Bulletin as PDF
            </button>

            {/* **Back to Date Selection** */}
            <button className="back-btn" onClick={() => setSelectedDate(null)}>
              🔙 Back to Date List
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsBulletin;
